import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../shared/LoadingSpinner';

export default function MFAChallengePage() {
  const { mfaChallenge, mfaVerify, signOut } = useAuth();
  const navigate = useNavigate();

  const [factorId, setFactorId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error: err } = await supabase.auth.mfa.listFactors();
      if (err || !data?.totp?.length) {
        // No MFA factors — redirect to enroll
        navigate('/mfa/enroll', { replace: true });
        return;
      }
      setFactorId(data.totp[0].id);
      setLoading(false);
    })();
  }, [navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    const { data: challengeData, error: challengeErr } = await mfaChallenge(factorId);
    if (challengeErr) {
      setError(challengeErr.message);
      setVerifying(false);
      return;
    }

    const { error: verifyErr } = await mfaVerify(factorId, challengeData.id, code);
    if (verifyErr) {
      setError('Invalid code. Please try again.');
      setCode('');
      setVerifying(false);
      return;
    }

    navigate('/app', { replace: true });
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[420px]">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
          <h1 className="text-[22px] font-bold text-primary-c">Two-factor authentication</h1>
          <p className="text-[13px] text-muted-c mt-1">Enter the code from your authenticator app</p>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          <form onSubmit={handleVerify} className="space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Verification Code</label>
              <input
                type="text"
                className={`${inputClass} text-center text-[18px] tracking-[0.3em] font-mono`}
                placeholder="000000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                autoFocus
                autoComplete="one-time-code"
              />
            </div>

            <button
              type="submit"
              disabled={verifying || code.length !== 6}
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {verifying ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={signOut}
              className="text-[12px] font-medium text-muted-c hover:text-danger transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
